import { FC } from "react";

import { Box, Badge, BoxProps, HtImage } from "@hightouch/htui";
import { Link } from "src/router";

import { IntegrationIcon } from "src/components/integrations/integration-icon";
import placeholderSource from "src/components/permission/source.svg";
import {
  SourceDefinitionFragment,
  SourceObjectDefinitionFragment,
  SourceObjectDefinitionsQueryVariables,
  SourceQuery,
  useSourceObjectDefinitionsQuery,
} from "src/graphql";
import { useSchemaDiscovery } from "./use-schema-discovery";

export type SourceConfig = Record<string, unknown>;

export type TableKeySource = {
  definition: Partial<
    Pick<SourceDefinitionFragment, "type" | "supportsCrossDatabaseDiscovery">
  >;
  config?: SourceConfig;
};

// Trino keys always name the catalog, which matches every saved Trino table model (`catalog.schema.table`).
export const includesCatalogInTableKey = (sourceType: string | undefined) =>
  sourceType === "trino";

// Trino discovery used to write group names as `catalog.schema`; the prefix is removed so both shapes give one key.
const stripDatabasePrefix = (groupName: string, database: string): string =>
  groupName.startsWith(`${database}.`)
    ? groupName.slice(database.length + 1)
    : groupName;

// Warehouses treat database names as case-insensitive.
export const getObjectDefinitionKey = (
  objectDefinition: Pick<
    SourceObjectDefinitionFragment,
    "name" | "object_definition_group"
  >,
  source?: TableKeySource,
): string => {
  const schemaGroup = objectDefinition.object_definition_group;
  if (!schemaGroup) {
    return objectDefinition.name;
  }
  const database = schemaGroup.database;
  if (includesCatalogInTableKey(source?.definition.type) && database) {
    return `${database}.${stripDatabasePrefix(schemaGroup.name, database)}.${objectDefinition.name}`;
  }
  const databaseConfigKey =
    source?.definition.type === "databricks" ? "catalog" : "database";
  const configuredDatabase = source?.config?.[databaseConfigKey];
  if (
    source?.definition.supportsCrossDatabaseDiscovery &&
    database &&
    typeof configuredDatabase === "string" &&
    database.toLowerCase() !== configuredDatabase.toLowerCase()
  ) {
    return `${database}.${schemaGroup.name}.${objectDefinition.name}`;
  }
  return `${schemaGroup.name}.${objectDefinition.name}`;
};

export type SourceTunnel = NonNullable<
  NonNullable<SourceQuery["connections_by_pk"]>["tunnel"]
>;

// Each dot-separated piece must match the database, schema, or table name.
export const parseSearch = (
  search?: string,
  options?: { matchDatabase?: boolean },
): Omit<SourceObjectDefinitionsQueryVariables, "sourceId"> => {
  const [first, second, ...rest] = search?.split(".") ?? [];
  const parts = [first, second, rest.join(".")].filter(Boolean);
  const terms = parts.map((part) => `%${part}%`);
  // '' matches no database; null would make the branch match-all.
  const databaseTerms = terms.map((term) =>
    options?.matchDatabase ? term : "",
  );
  return {
    term1: terms[0],
    term2: terms[1],
    term3: terms[2],
    termDatabase1: databaseTerms[0],
    termDatabase2: databaseTerms[1],
    termDatabase3: databaseTerms[2],
  };
};

export const useObjectDefinitions = (
  sourceId: string | undefined,
  enable = true,
  search?: string,
  options?: { matchDatabase?: boolean; sourceType?: string },
) => {
  // A Trino group name may be schema-only, so a search for its catalog has to match the group's database.
  const matchDatabase =
    options?.matchDatabase || includesCatalogInTableKey(options?.sourceType);
  const {
    data: objectDefinitionsData,
    isFetching: objectDefinitionsLoading,
    error: objectDefinitionsError,
    refetch: refetchObjectDefinitions,
  } = useSourceObjectDefinitionsQuery(
    { sourceId: sourceId ?? "", ...parseSearch(search, { matchDatabase }) },
    { enabled: Boolean(sourceId) && enable },
  );

  const { discovering, runSourceDiscovery, discoverError } = useSchemaDiscovery(
    {
      connectionId: sourceId ?? "",
    },
  );

  // Trino group names are `catalog.schema`, so the query's HIGHTOUCH_PLANNER exclusion misses them and Trino's Lightning schemas can appear in pickers.
  // That exclusion is already best-effort because planner and audit schema names are configurable per source, so this is acceptable.
  const objectDefinitions = objectDefinitionsData?.object_definitions;

  const objectDefinitionOptions =
    objectDefinitions?.map((objectDefinition) => {
      const key = getObjectDefinitionKey(objectDefinition, {
        definition: { type: options?.sourceType },
      });
      return { label: key, value: key };
    }) || [];

  const refetch = async () => {
    try {
      if (sourceId) {
        await runSourceDiscovery();
      }
      refetchObjectDefinitions();
    } catch (error) {
      console.log(error);
    }
  };

  return {
    refetch,
    objectDefinitions,
    objectDefinitionOptions,
    loading: discovering || objectDefinitionsLoading,
    error: (discoverError || objectDefinitionsError)?.message,
  };
};

type SourceTileProps = {
  source?: {
    name: string;
    id: string;
    definition: {
      name: string;
      icon: string;
      isSampleDataSource: boolean;
    };
  };
  placeholder?: boolean;
};

export const SourceTile: FC<Readonly<SourceTileProps>> = ({
  source,
  placeholder,
}) => {
  const name = !source && placeholder ? "Private source" : source?.name;

  return (
    <>
      <IntegrationIcon
        name={source?.definition?.name}
        src={source?.definition?.icon}
      />
      <Box
        sx={{
          fontWeight: "semi",
          fontSize: "lg",
          ml: 2,
          a: { color: "inherit", fontSize: "inherit", fontWeight: "inherit" },
        }}
      >
        {source?.id ? (
          <Link href={`/sources/${source?.id}`}>{name}</Link>
        ) : (
          name
        )}
      </Box>

      <SourceBadges
        isSampleDataSource={source?.definition.isSampleDataSource}
      />
    </>
  );
};

export function SourceDefinitionIcon({
  placeholder,
  source,
  sx = {},
}: Readonly<{
  placeholder?: boolean;
  source:
    | {
        definition: {
          name: string;
          icon: string;
        };
      }
    | undefined
    | null;
  sx: BoxProps["sx"];
}>): JSX.Element | null {
  const alt =
    placeholder && !source ? "Private source" : source?.definition?.name;
  const src =
    placeholder && !source ? placeholderSource : source?.definition?.icon;
  return (
    <HtImage
      alt={alt || ""}
      src={src || ""}
      sx={{ ...sx, objectFit: "contain" }}
    />
  );
}

export function SourceBadges({
  isSampleDataSource,
}: Readonly<{
  isSampleDataSource: boolean | undefined;
  sx?: BoxProps["sx"];
}>): JSX.Element | null {
  return isSampleDataSource ? (
    <Badge ml={2} variant="warning">
      Sample
    </Badge>
  ) : null;
}
